import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  countryFlagClass,
  useLocalizedCountries,
  type Country,
} from '@/lib/countries';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

function CountryList({
  filtered,
  value,
  highlight,
  listRef,
  onSelect,
  onClear,
  onHighlight,
}: {
  filtered: Country[];
  value: string;
  highlight: number;
  listRef: React.RefObject<HTMLDivElement | null>;
  onSelect: (code: string) => void;
  onClear: () => void;
  onHighlight: (i: number) => void;
}) {
  const { t } = useTranslation('common');
  return (
    <div ref={listRef} className="flex-1 overflow-y-auto p-1">
      {value && (
        <button
          type="button"
          className="flex w-full items-center rounded px-2 py-1.5 text-sm hover:bg-accent"
          onClick={onClear}
        >
          <span className="text-muted-foreground">{t('country_select.clear')}</span>
        </button>
      )}
      {filtered.map((c, i) => (
        <button
          key={c.code}
          type="button"
          data-country-item
          className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent ${
            i === highlight
              ? 'bg-accent'
              : c.code === value.toUpperCase()
                ? 'bg-accent/50'
                : ''
          }`}
          onClick={() => onSelect(c.code)}
          onMouseEnter={() => onHighlight(i)}
        >
          <span className={countryFlagClass(c.code)} />
          <span className="truncate">{c.name}</span>
          <span className="ml-auto shrink-0 text-xs text-muted-foreground">{c.code}</span>
        </button>
      ))}
      {filtered.length === 0 && (
        <p className="px-2 py-1.5 text-sm text-muted-foreground">{t('country_select.no_matches')}</p>
      )}
    </div>
  );
}

export function CountrySelect({
  value,
  onChange,
  className,
  disabled,
  'data-testid': testId,
}: {
  value: string;
  onChange: (code: string) => void;
  className?: string;
  disabled?: boolean;
  'data-testid'?: string;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlight, setHighlight] = useState(-1);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const mobileListRef = useRef<HTMLDivElement>(null);

  const countries = useLocalizedCountries();

  const selected = countries.find((c) => c.code === value.toUpperCase());

  const filtered = countries.filter((c) => {
    const q = search.toLowerCase();
    return (
      c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    setHighlight(-1);
  }, [search]);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setSearch('');
    setHighlight(-1);
  }, []);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        buttonRef.current?.contains(e.target as Node) ||
        dropdownRef.current?.contains(e.target as Node) ||
        mobileRef.current?.contains(e.target as Node)
      ) return;
      closeDropdown();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, closeDropdown]);

  function handleOpen() {
    if (open) {
      closeDropdown();
      return;
    }
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen(true);
    setHighlight(-1);
    setTimeout(() => {
      inputRef.current?.focus();
      mobileInputRef.current?.focus();
    }, 0);
  }

  function handleSelect(code: string) {
    onChange(code);
    closeDropdown();
  }

  function handleClear() {
    onChange('');
    closeDropdown();
  }

  function scrollToHighlight(index: number) {
    for (const ref of [listRef, mobileListRef]) {
      const items = ref.current?.querySelectorAll('[data-country-item]');
      if (items && items[index]) {
        items[index].scrollIntoView({ block: 'nearest' });
      }
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.min(highlight + 1, filtered.length - 1);
      setHighlight(next);
      scrollToHighlight(next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.max(highlight - 1, 0);
      setHighlight(next);
      scrollToHighlight(next);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight >= 0 && highlight < filtered.length) {
        handleSelect(filtered[highlight].code);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown();
      buttonRef.current?.focus();
    }
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        className={`flex h-9 w-full items-center rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs truncate disabled:pointer-events-none disabled:opacity-50 dark:bg-input/30 ${className ?? ''}`}
        onClick={disabled ? undefined : handleOpen}
        onKeyDown={(e) => {
          if (!open && e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            setSearch(e.key);
            handleOpen();
          }
        }}
        {...(testId ? { 'data-testid': testId } : {})}
      >
        {selected ? (
          <span className="flex items-center gap-1.5 truncate">
            <span className={countryFlagClass(selected.code)} />
            {selected.name}
          </span>
        ) : (
          <span className="text-muted-foreground">{t('country_select.placeholder')}</span>
        )}
      </button>

      {open && (
        <>
          {/* Desktop: fixed dropdown */}
          <div
            ref={dropdownRef}
            className="fixed z-[100] hidden w-64 rounded-md border bg-popover shadow-md md:block"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="p-1">
              <Input
                ref={inputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('country_select.search_placeholder')}
                className="h-8 text-sm"
              />
            </div>
            <CountryList
              filtered={filtered}
              value={value}
              highlight={highlight}
              listRef={listRef}
              onSelect={handleSelect}
              onClear={handleClear}
              onHighlight={setHighlight}
            />
          </div>

          {/* Mobile: full-screen picker */}
          <div ref={mobileRef} className="fixed inset-0 z-[100] flex flex-col bg-background md:hidden">
            <header className="flex items-center justify-between border-b px-4 py-3">
              <h2 className="text-lg font-semibold">{t('country_select.modal_title')}</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeDropdown}
              >
                <X className="h-5 w-5" />
              </Button>
            </header>
            <div className="p-2">
              <Input
                ref={mobileInputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('country_select.search_placeholder')}
                className="h-10 text-base"
              />
            </div>
            <CountryList
              filtered={filtered}
              value={value}
              highlight={highlight}
              listRef={mobileListRef}
              onSelect={handleSelect}
              onClear={handleClear}
              onHighlight={setHighlight}
            />
          </div>
        </>
      )}
    </>
  );
}
